import { Suspense, type ComponentType } from 'react';
import { lazyWithRetry } from './lazyWithRetry';
import type { RouteObject } from 'react-router-dom';
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { AuthGuard } from './AuthGuard';
import { RoleGuard } from './RoleGuard';
import { OnboardingGuard } from './OnboardingGuard';
import { ProtectedLayout } from '@layouts/ProtectedLayout';
import ProviderHomeRedirect from '@components/ProviderHomeRedirect';
import { selectWorklistView } from './worklistRouteUtils';

const PracticeHomePage = lazyWithRetry(() => import('@pages/PracticeHomePage'));
const SetupPracticePage = lazyWithRetry(() => import('@pages/SetupPracticePage'));
const ConfigurePracticePage = lazyWithRetry(() => import('@pages/ConfigurePracticePage'));
const UnderConstructionPage = lazyWithRetry(() => import('@pages/UnderConstructionPage'));
const DataPage = lazyWithRetry(() => import('@pages/DataPage'));
const DataHistoryPage = lazyWithRetry(() => import('@pages/DataHistoryPage'));
const ConfigureDataPage = lazyWithRetry(() => import('@pages/configure/ConfigureDataPage'));
const FeatureConfigPage = lazyWithRetry(() => import('@pages/configure/FeatureConfigPage'));
const SurveysConfigPage = lazyWithRetry(() => import('@pages/configure/SurveysConfigPage'));
const SurveyTemplateConfigPage = lazyWithRetry(() => import('@pages/configure/SurveyTemplateConfigPage'));
const SurveyTemplateEditPage = lazyWithRetry(() => import('@pages/configure/SurveyTemplateEditPage'));
const SurveyScheduleConfigPage = lazyWithRetry(() => import('@pages/configure/SurveyScheduleConfigPage'));
const SurveyAnalyticsPage = lazyWithRetry(() => import('@pages/SurveyAnalyticsPage'));
const VisitAnalyticsPage = lazyWithRetry(() => import('@pages/VisitAnalyticsPage'));
const BillingAnalyticsPage = lazyWithRetry(() => import('@pages/BillingAnalyticsPage'));
const BillingConfigPracticePage = lazyWithRetry(() => import('@pages/BillingConfigPracticePage'));
const ProviderScorecardPage = lazyWithRetry(() => import('@pages/ProviderScorecardPage'));
const VisitAlarmsPage = lazyWithRetry(() => import('@pages/VisitAlarmsPage'));
const VisitConfigAdminPage = lazyWithRetry(() => import('@pages/VisitConfigAdminPage'));
const VisitConfigPracticePage = lazyWithRetry(() => import('@pages/VisitConfigPracticePage'));
const MessagingDomainsPage = lazyWithRetry(() => import('@pages/configure/MessagingDomainsPage'));
const DataCatalogPage = lazyWithRetry(() => import('@pages/DataCatalogPage'));
const AppointmentSurveysListPage = lazyWithRetry(() => import('@pages/AppointmentSurveysListPage'));
const SurveyDetailPage = lazyWithRetry(() => import('@pages/SurveyDetailPage'));
const AdminDashboardPage = lazyWithRetry(() => import('@pages/AdminDashboardPage'));
const AdpFieldListPage = lazyWithRetry(() => import('@pages/AdpFieldListPage'));
const AddAdpFieldPage = lazyWithRetry(() => import('@pages/AddAdpFieldPage'));
const AdpFieldDetailPage = lazyWithRetry(() => import('@pages/AdpFieldDetailPage'));
const ReviewPlatformManagerPage = lazyWithRetry(() => import('@pages/ReviewPlatformManagerPage'));
const ScheduleRunnerConfigPage = lazyWithRetry(() => import('@pages/admin/ScheduleRunnerConfigPage'));
const GlobalTemplatesPage = lazyWithRetry(() => import('@pages/admin/GlobalTemplatesPage'));
const GlobalQuestionsPage = lazyWithRetry(() => import('@pages/admin/GlobalQuestionsPage'));
const OnboardingPathPage = lazyWithRetry(() => import('@pages/OnboardingPathPage'));
const JoinPracticePage = lazyWithRetry(() => import('@pages/JoinPracticePage'));
const PendingApprovalPage = lazyWithRetry(() => import('@pages/PendingApprovalPage'));
const NoAccessPage = lazyWithRetry(() => import('@pages/NoAccessPage'));
const UserManagementPage = lazyWithRetry(() => import('@pages/UserManagementPage'));
const RegistrationPendingPage = lazyWithRetry(() => import('@pages/RegistrationPendingPage'));
const RegistrationRejectedPage = lazyWithRetry(() => import('@pages/RegistrationRejectedPage'));
const PracticeApprovalsPage = lazyWithRetry(() => import('@pages/admin/PracticeApprovalsPage'));
const RoleManagementPage = lazyWithRetry(() => import('@pages/admin/RoleManagementPage'));
const FieldMappingListPage = lazyWithRetry(() => import('@pages/configure/FieldMappingListPage'));
const FieldMappingEditorPage = lazyWithRetry(() => import('@pages/configure/FieldMappingEditorPage'));
const AdminDataCatalogPage = lazyWithRetry(() => import('@pages/admin/AdminDataCatalogPage'));
const ClassMappingAdminPage = lazyWithRetry(() => import('@pages/admin/ClassMappingAdminPage'));
const EmailReportsPracticePage = lazyWithRetry(() => import('@pages/EmailReportsPracticePage'));
const EmailReportsAdminPage = lazyWithRetry(() => import('@pages/admin/EmailReportsAdminPage'));
const MetricsListPage = lazyWithRetry(() => import('@pages/MetricsListPage'));
const MetricDetailPage = lazyWithRetry(() => import('@pages/MetricDetailPage'));

const MetricsAdminPage = lazyWithRetry(() => import('@pages/admin/MetricsAdminPage'));
const ComputedMetricsAdminPage = lazyWithRetry(() => import('@pages/admin/ComputedMetricsAdminPage'));
const SystemEventsPage = lazyWithRetry(() => import('@pages/admin/SystemEventsPage'));
const DefaultComponentAccessPage = lazyWithRetry(() => import('@pages/admin/DefaultComponentAccessPage'));
const AlarmDailyReportSchedulerPage = lazyWithRetry(() => import('@pages/admin/AlarmDailyReportSchedulerPage'));
const DataLoadDisplayColumnsPage = lazyWithRetry(() => import('@pages/admin/DataLoadDisplayColumnsPage'));
const ComputedMetricListPage = lazyWithRetry(() => import('@pages/computedMetrics/ComputedMetricListPage'));
const ComputedMetricDetailPage = lazyWithRetry(() => import('@pages/computedMetrics/ComputedMetricDetailPage'));
const AlarmsAndFindingsPage = lazyWithRetry(() => import('@pages/alarms/AlarmsAndFindingsPage'));
const AlarmDetailPage = lazyWithRetry(() => import('@pages/alarms/AlarmDetailPage'));
const WorklistsListPage = lazyWithRetry(() => import('@pages/worklists/WorklistsListPage'));
const WorklistDetailPage = lazyWithRetry(() =>
  // WorklistDetailPage reads `?worklistId` from useSearchParams itself, so it
  // mounts with no props on this route; coerce away its optional prop type.
  import('@pages/worklists/WorklistDetailPage').then((m) => ({
    default: m.default as ComponentType<unknown>,
  })),
);
const GoalSetsListPage = lazyWithRetry(() => import('@pages/goalSets/GoalSetsListPage'));
const GoalSetDetailPage = lazyWithRetry(() => import('@pages/goalSets/GoalSetDetailPage'));
const GoalDetailPage = lazyWithRetry(() => import('@pages/goalSets/GoalDetailPage'));
const TaskDetailPage = lazyWithRetry(() => import('@pages/goalSets/TaskDetailPage'));
const IssueDetailPage = lazyWithRetry(() => import('@pages/workTracking/IssueDetailPage'));
const RiskDetailPage = lazyWithRetry(() => import('@pages/workTracking/RiskDetailPage'));
const IdeaDetailPage = lazyWithRetry(() => import('@pages/workTracking/IdeaDetailPage'));
const QuestionDetailPage = lazyWithRetry(() => import('@pages/workTracking/QuestionDetailPage'));
const OpportunityDetailPage = lazyWithRetry(() => import('@pages/workTracking/OpportunityDetailPage'));
const WorkTaskDetailPage = lazyWithRetry(() => import('@pages/workTracking/WorkTaskDetailPage'));
const DepartmentDetailPage = lazyWithRetry(() => import('@pages/workTracking/DepartmentDetailPage'));
const RemindersListPage = lazyWithRetry(() => import('@pages/reminders/RemindersListPage'));
const ReminderDetailPage = lazyWithRetry(() => import('@pages/reminders/ReminderDetailPage'));
const HrPage = lazyWithRetry(() => import('@pages/hr/HrPage'));
const HrResourceDetailPage = lazyWithRetry(() => import('@pages/hr/HrResourceDetailPage'));
const HrResourceCreatePage = lazyWithRetry(() => import('@pages/hr/HrResourceCreatePage'));
const HrConfigPage = lazyWithRetry(() => import('@pages/hr/HrConfigPage'));
const DepartmentsConfigPage = lazyWithRetry(() => import('@pages/configure/DepartmentsConfigPage'));

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-[50vh] items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

function FieldMappingRedirect() {
  // The legacy survey field-mappings route carries the canonical-level `field_name`
  // (unique among Global_Field_Rows per adp-canonical-registry Property 16); pass it
  // through unchanged to the canonical `/configure/data/field-mappings/:fieldName` editor.
  const { fieldName } = useParams<{ fieldName: string }>();
  return <Navigate to={`/configure/data/field-mappings/${fieldName}`} replace />;
}

/**
 * Generic legacy-path redirect to a canonical Component_Entry_Route
 * (component-access-control Decision 8, Req 19.2). Forwards to `to` while
 * preserving any query string on the incoming URL. Route params are not part
 * of `to`; use a dedicated redirect component when the canonical target embeds
 * a param.
 */
function LegacyRedirect({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

/**
 * Redirects the legacy `/analytics/metrics/:metricId` detail path to the
 * canonical `/metrics/:metricId`, preserving the route param and query string.
 */
function MetricDetailRedirect() {
  const { metricId = '' } = useParams<{ metricId: string }>();
  const { search } = useLocation();
  return <Navigate to={`/metrics/${encodeURIComponent(metricId)}${search}`} replace />;
}

/**
 * Redirects the legacy computed-metric detail paths
 * (`/analytics/computed-metrics/:id` and `/computed-metrics/:id`) to the
 * canonical owner-only `/metrics/computed-metrics/:id`, preserving the route
 * param and query string.
 */
function ComputedMetricDetailRedirect() {
  const { computedMetricId = '' } = useParams<{ computedMetricId: string }>();
  const { search } = useLocation();
  return (
    <Navigate
      to={`/metrics/computed-metrics/${encodeURIComponent(computedMetricId)}${search}`}
      replace
    />
  );
}

/**
 * `/alarms/:alarmId` is a convenience route preserved for existing deep
 * links (e.g. medrender-owner alarm links rendered in the legacy
 * Heuristic detail drawer). It canonicalizes to the integrated
 * `/alarms?alarmId=...` URL so the orchestrator picks the alarm up via
 * its `useSearchParams` source of truth without a second mounted page.
 */
function AlarmIdRedirect() {
  const { alarmId = '' } = useParams<{ alarmId: string }>();
  return (
    <Navigate
      to={`/alarms?alarmId=${encodeURIComponent(alarmId)}`}
      replace
    />
  );
}

/**
 * The `/worklists` base route serves both views (worklist-upload-visibility
 * Req 4.1–4.4). A non-empty `?worklistId=<id>` selects the
 * Worklist_Detail_View; otherwise the Worklist_List_View renders. The view is
 * chosen by the pure `selectWorklistView` helper so the branch is
 * property-testable without mounting the router.
 */
function WorklistsRouteElement() {
  const [searchParams] = useSearchParams();
  const view = selectWorklistView(searchParams);
  // Both `'item'` and `'detail'` render WorklistDetailPage: it owns the
  // Filtered_Sorted_Items model, the total count and the filter bar, and it
  // renders the Work_Item_Detail_View in place when `?itemId=` is present.
  // Routing `'item'` to a separate page unmounted this one, which destroyed the
  // filter state and the item sequence — leaving the detail view unable to show
  // the active filters, the total, or a prev/next pager over the current view.
  if (view === 'item' || view === 'detail') return <WorklistDetailPage />;
  return <WorklistsListPage />;
}

export const protectedRoutes: RouteObject[] = [
  {
    element: (
      <AuthGuard>
        <ProtectedLayout />
      </AuthGuard>
    ),
    children: [
      {
        path: 'home',
        element: <RoleGuard><SuspenseWrapper><ProviderHomeRedirect><PracticeHomePage /></ProviderHomeRedirect></SuspenseWrapper></RoleGuard>,
      },
      {
        // `/analytics` overview is retired (component-access-control
        // Decision 8); the dashboard grid lives at `/home`. Redirect legacy
        // bookmarks, preserving any query string.
        path: 'analytics',
        element: <LegacyRedirect to="/home" />,
      },
      {
        path: 'setup-practice',
        element: <OnboardingGuard><SuspenseWrapper><SetupPracticePage /></SuspenseWrapper></OnboardingGuard>,
      },
      {
        path: 'configure',
        element: <RoleGuard><SuspenseWrapper><ConfigurePracticePage /></SuspenseWrapper></RoleGuard>,
      },
      {
        path: 'configure/surveys',
        element: <RoleGuard><SuspenseWrapper><SurveysConfigPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        path: 'configure/surveys/template/new',
        element: <RoleGuard><SuspenseWrapper><SurveyTemplateEditPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        path: 'configure/surveys/template/:templateId/edit',
        element: <RoleGuard><SuspenseWrapper><SurveyTemplateEditPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        path: 'configure/surveys/template/:templateId',
        element: <RoleGuard><SuspenseWrapper><SurveyTemplateConfigPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        path: 'configure/surveys/schedule/:scheduleId',
        element: <RoleGuard><SuspenseWrapper><SurveyScheduleConfigPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        path: 'configure/surveys/field-mappings',
        element: <Navigate to="/configure/data/field-mappings" replace />,
      },
      {
        path: 'configure/surveys/field-mappings/:fieldName',
        element: <FieldMappingRedirect />,
      },
      {
        path: 'configure/adp-fields',
        element: <Navigate to="/configure/data" replace />,
      },
      {
        path: 'configure/visits',
        element: <RoleGuard><SuspenseWrapper><VisitConfigPracticePage /></SuspenseWrapper></RoleGuard>,
      },
      {
        // Billing_Configuration_Page (billing-collections-module Req 19.2, 19.4).
        // Registered without `requiredGroups`, exactly like `configure/visits`
        // above: the catalog's `billing` entry declares
        // `configSubRoutes: ['/configure/billing']`, and `RoleGuard` restricts any
        // Config_Sub_Route path to practice-owner / medrender-owner before it
        // consults the Access_Policy — so this is owner-only and, unlike the
        // `/billing` entry route, is never grantable through the Access_Matrix.
        path: 'configure/billing',
        element: <RoleGuard><SuspenseWrapper><BillingConfigPracticePage /></SuspenseWrapper></RoleGuard>,
      },
      {
        path: 'configure/email-reports',
        element: <RoleGuard><SuspenseWrapper><EmailReportsPracticePage /></SuspenseWrapper></RoleGuard>,
      },
      {
        path: 'configure/departments',
        element: <RoleGuard><SuspenseWrapper><DepartmentsConfigPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        path: 'configure/metrics',
        element: <Navigate to="/metrics" replace />,
      },
      {
        path: 'configure/computed-metrics',
        element: <Navigate to="/metrics" replace />,
      },
      {
        path: 'configure/alarms',
        element: <Navigate to="/alarms?tab=alarms" replace />,
      },
      // --- Canonical Component entry routes (component-access-control
      // Decision 8). Each Feature_Card_Component has one top-level entry
      // route; the former `/analytics/*` and flat computed-metrics paths now
      // redirect here, preserving route params and any query string. ---
      {
        path: 'surveys',
        element: <RoleGuard><SuspenseWrapper><SurveyAnalyticsPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        path: 'visits',
        element: <RoleGuard><SuspenseWrapper><VisitAnalyticsPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        path: 'provider-scorecard',
        element: <RoleGuard><SuspenseWrapper><ProviderScorecardPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        // Central Visit_Alarms_Page (visit-alarms Req 7.1, 12.3). Practice-owner-
        // only surface for managing provider-scoped visit alarm rules, recipients,
        // and recent breaches; gated with the same owner-only group set as the
        // other owner-only feature routes here (e.g. `/alarms`,
        // `/metrics/computed-metrics`) so a provider-user cannot reach it.
        path: 'visit-alarms',
        element: (
          <RoleGuard requiredGroups={['practice-owner', 'medrender-owner']}>
            <SuspenseWrapper><VisitAlarmsPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        // Billing_Analytics_Page (billing-collections-module Req 2.4). Gating
        // derives from the catalog's `billing` entry, whose `guards` is `[]`, so
        // this carries no `requiredGroups` — like `/visits` and `/metrics`, it is
        // open to non-owners under the Entry_Gate. The owner-only
        // `/configure/billing` Config_Sub_Route is registered separately.
        path: 'billing',
        element: <RoleGuard><SuspenseWrapper><BillingAnalyticsPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        path: 'metrics',
        element: <RoleGuard><SuspenseWrapper><MetricsListPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        // Computed_Metrics is an owner-only Config_Sub_Route nested under
        // Metrics (Decision 8). Registered ahead of `/metrics/:metricId` so the
        // static `computed-metrics` segment is matched before the param route.
        path: 'metrics/computed-metrics',
        element: (
          <RoleGuard requiredGroups={['practice-owner', 'medrender-owner']}>
            <SuspenseWrapper><ComputedMetricListPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'metrics/computed-metrics/:computedMetricId',
        element: (
          <RoleGuard requiredGroups={['practice-owner', 'medrender-owner']}>
            <SuspenseWrapper><ComputedMetricDetailPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'metrics/:metricId',
        element: <RoleGuard><SuspenseWrapper><MetricDetailPage /></SuspenseWrapper></RoleGuard>,
      },
      // --- Legacy `/analytics/*` and flat computed-metrics redirects ---
      {
        path: 'analytics/surveys',
        element: <LegacyRedirect to="/surveys" />,
      },
      {
        path: 'analytics/visits',
        element: <LegacyRedirect to="/visits" />,
      },
      {
        path: 'analytics/provider-scorecard',
        element: <LegacyRedirect to="/provider-scorecard" />,
      },
      {
        path: 'analytics/heuristics',
        element: <Navigate to="/alarms" replace />,
      },
      {
        path: 'analytics/metrics',
        element: <LegacyRedirect to="/metrics" />,
      },
      {
        path: 'analytics/metrics/:metricId',
        element: <MetricDetailRedirect />,
      },
      {
        path: 'analytics/computed-metrics/:computedMetricId',
        element: <ComputedMetricDetailRedirect />,
      },
      {
        path: 'computed-metrics',
        element: <LegacyRedirect to="/metrics/computed-metrics" />,
      },
      {
        path: 'computed-metrics/:computedMetricId',
        element: <ComputedMetricDetailRedirect />,
      },
      {
        path: 'alarms',
        element: (
          <RoleGuard requiredGroups={['practice-owner', 'medrender-owner']}>
            <SuspenseWrapper><AlarmsAndFindingsPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        // Convenience route — `/alarms/:alarmId` redirects to the integrated
        // page with the alarm pre-applied via the `alarmId` query param so
        // existing deep links (e.g. from the Heuristic detail drawer's
        // medrender-owner alarm link) keep working.
        path: 'alarms/:alarmId',
        element: (
          <RoleGuard requiredGroups={['practice-owner', 'medrender-owner']}>
            <AlarmIdRedirect />
          </RoleGuard>
        ),
      },
      {
        // Standalone detail page — surfaces the full state-transition
        // timeline and definition view that doesn't fit inline in the
        // Alarms list. Reached via the "View full detail" link from the
        // expanded row on the integrated page.
        path: 'alarms/:alarmId/detail',
        element: (
          <RoleGuard requiredGroups={['practice-owner', 'medrender-owner']}>
            <SuspenseWrapper><AlarmDetailPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        // Worklist_List_View / Worklist_Detail_View (worklist-upload-
        // visibility Req 4.1–4.4). `WorklistsRouteElement` branches on the
        // `?worklistId=<id>` query param: present → detail, absent → list.
        // Visible to practice members; create/edit/enable controls are gated to
        // owners inside the list page.
        path: 'worklists',
        element: <RoleGuard><SuspenseWrapper><WorklistsRouteElement /></SuspenseWrapper></RoleGuard>,
      },
      {
        // Goal_Set_List_View (spec design §11). Visible to practice members;
        // create control and per-row open control live inside the page. Global
        // copy-candidates render read-only.
        path: 'goal-sets',
        element: <RoleGuard><SuspenseWrapper><GoalSetsListPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        // Goal_Set_Detail_View (spec design §11). `:goalSetId` selects the
        // Goal_Set; a `global` set renders read-only with "Copy to my practice".
        // More specific Goal/Task drill-down routes are registered first so
        // react-router matches them ahead of this base route.
        path: 'goal-sets/:goalSetId/goals/:goalId/tasks/:taskId',
        element: <RoleGuard><SuspenseWrapper><TaskDetailPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        // Goal_Detail_View (spec design §11). `:goalId` selects the Goal out of
        // the parent Goal_Set's tree; shows the Goal editor + its Task list.
        path: 'goal-sets/:goalSetId/goals/:goalId',
        element: <RoleGuard><SuspenseWrapper><GoalDetailPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        // Goal_Set_Detail_View (spec design §11). `:goalSetId` selects the
        // Goal_Set; a `global` set renders read-only with "Copy to my practice".
        path: 'goal-sets/:goalSetId',
        element: <RoleGuard><SuspenseWrapper><GoalSetDetailPage /></SuspenseWrapper></RoleGuard>,
      },
      // --- Work_Tracking flat Entity_Detail_View routes (work-tracking design
      // § Frontend, Req 10.1). Because a Task is no longer nested under a single
      // parent, Issues/Risks/Ideas/Tasks are each reachable through a flat
      // route. Each is its own distinct top-level segment (`issues`/`risks`/
      // `ideas`/`tasks`), so it neither shadows nor is shadowed by another
      // route; they are listed here, most-specific first, alongside the
      // Goal_Sets drill-down routes above. The Tasks route carries its
      // polymorphic parent reference in the query string
      // (`?parentType=&parentId=[&goalSetId=&goalId=]`, design § HTTP API). ---
      {
        // Issue Entity_Detail_View — `/issues/:issueId` (Req 10.1, 10.3).
        path: 'issues/:issueId',
        element: <RoleGuard><SuspenseWrapper><IssueDetailPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        // Risk Entity_Detail_View — `/risks/:riskId` (Req 10.1, 10.3).
        path: 'risks/:riskId',
        element: <RoleGuard><SuspenseWrapper><RiskDetailPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        // Idea Entity_Detail_View — `/ideas/:ideaId` (Req 10.1, 10.3).
        path: 'ideas/:ideaId',
        element: <RoleGuard><SuspenseWrapper><IdeaDetailPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        // Question Entity_Detail_View — `/questions/:questionId` (Q Req 1).
        path: 'questions/:questionId',
        element: <RoleGuard><SuspenseWrapper><QuestionDetailPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        // Opportunity Entity_Detail_View — `/opportunities/:opportunityId` (O Req 2).
        path: 'opportunities/:opportunityId',
        element: <RoleGuard><SuspenseWrapper><OpportunityDetailPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        // Polymorphic Task Entity_Detail_View — `/tasks/:taskId` with the parent
        // reference in the query string (Req 6.5, 10.1, 10.2).
        path: 'tasks/:taskId',
        element: <RoleGuard><SuspenseWrapper><WorkTaskDetailPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        // Department_Detail_View — `/departments/:departmentId` (Req 1.17, 10.1,
        // 10.8). Viewable by any Practice_Member (Req 1.15); the grouped items
        // are visibility-filtered by the backend (Req 1.18).
        path: 'departments/:departmentId',
        element: <RoleGuard><SuspenseWrapper><DepartmentDetailPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        // Reminders_Api_List_View (reminders spec design §12). Visible to
        // practice members; the "Assigned to Me" checkbox filters the list and
        // a row click navigates to the Reminders_Detail_View.
        path: 'reminders',
        element: <RoleGuard><SuspenseWrapper><RemindersListPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        // Reminders_Detail_View (reminders spec design §12). `:reminderId`
        // selects the Reminder; shows the acknowledge control + Activity_Timeline.
        path: 'reminders/:reminderId',
        element: <RoleGuard><SuspenseWrapper><ReminderDetailPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        // HR_Resource_List_View (hr-module Req 1.1, 1.6, 19.9). Practice-
        // owner or medrender-owner only; a caller who holds neither group
        // receives the unauthorized experience.
        path: 'hr',
        element: (
          <RoleGuard requiredGroups={['practice-owner', 'medrender-owner']}>
            <SuspenseWrapper><HrPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        // HR_Resource_Create_Page — `/hr/resources/new`. Registered before the
        // param route so `new` is not treated as an hrResourceId.
        path: 'hr/resources/new',
        element: (
          <RoleGuard requiredGroups={['practice-owner', 'medrender-owner']}>
            <SuspenseWrapper><HrResourceCreatePage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        // HR_Resource_Detail_View (hr-module Req 1.1, 1.6, 19.9).
        path: 'hr/resources/:hrResourceId',
        element: (
          <RoleGuard requiredGroups={['practice-owner', 'medrender-owner']}>
            <SuspenseWrapper><HrResourceDetailPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        // HR_Configuration_Section (hr-module Req 1.1, 1.6, 19.9). Practice-
        // owner or medrender-owner only; gated identically to the HR routes.
        path: 'configure/hr',
        element: (
          <RoleGuard requiredGroups={['practice-owner', 'medrender-owner']}>
            <SuspenseWrapper><HrConfigPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'configure/messaging',
        element: <RoleGuard><SuspenseWrapper><MessagingDomainsPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        path: 'configure/appointments',
        element: <RoleGuard><SuspenseWrapper><UnderConstructionPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        path: 'configure/data',
        element: <RoleGuard><SuspenseWrapper><ConfigureDataPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        path: 'configure/data/field-mappings',
        element: <RoleGuard><SuspenseWrapper><FieldMappingListPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        path: 'configure/data/field-mappings/:fieldName',
        element: <RoleGuard><SuspenseWrapper><FieldMappingEditorPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        path: 'data',
        element: <RoleGuard><SuspenseWrapper><DataPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        path: 'data/history',
        element: <RoleGuard><SuspenseWrapper><DataHistoryPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        path: 'data/catalog/:dataloadId',
        element: <RoleGuard><SuspenseWrapper><DataCatalogPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        path: 'surveys/all',
        element: <RoleGuard><SuspenseWrapper><AppointmentSurveysListPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        path: 'surveys/:templateId/results',
        element: <RoleGuard><SuspenseWrapper><AppointmentSurveysListPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        path: 'surveys/schedule/:scheduleId',
        element: <RoleGuard><SuspenseWrapper><AppointmentSurveysListPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        path: 'surveys/:templateId/results/:surveyId',
        element: <RoleGuard><SuspenseWrapper><SurveyDetailPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        path: 'configure/service/:featureId',
        element: <RoleGuard><SuspenseWrapper><FeatureConfigPage /></SuspenseWrapper></RoleGuard>,
      },
      {
        path: 'admin',
        element: (
          <RoleGuard requiredGroups={['medrender-owner']}>
            <SuspenseWrapper><AdminDashboardPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'admin/adp-fields',
        element: (
          <RoleGuard requiredGroups={['medrender-owner']}>
            <SuspenseWrapper><AdpFieldListPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'admin/adp-fields/new',
        element: (
          <RoleGuard requiredGroups={['medrender-owner']}>
            <SuspenseWrapper><AddAdpFieldPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'admin/adp-fields/:dataloadId/:adpId',
        element: (
          <RoleGuard requiredGroups={['medrender-owner']}>
            <SuspenseWrapper><AdpFieldDetailPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        // Legacy load-less detail route. A slug can appear in more than one
        // Data_Load, so this route is ambiguous — redirect to the load-scoped
        // list rather than resolving to the wrong field.
        path: 'admin/adp-fields/:adpId',
        element: (
          <RoleGuard requiredGroups={['medrender-owner']}>
            <Navigate to="/admin/adp-fields" replace />
          </RoleGuard>
        ),
      },
      {
        path: 'admin/review-platforms',
        element: (
          <RoleGuard requiredGroups={['medrender-owner']}>
            <SuspenseWrapper><ReviewPlatformManagerPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'admin/schedule-runner',
        element: (
          <RoleGuard requiredGroups={['medrender-owner']}>
            <SuspenseWrapper><ScheduleRunnerConfigPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'admin/global-templates',
        element: (
          <RoleGuard requiredGroups={['medrender-owner']}>
            <SuspenseWrapper><GlobalTemplatesPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'admin/global-questions',
        element: (
          <RoleGuard requiredGroups={['medrender-owner']}>
            <SuspenseWrapper><GlobalQuestionsPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'onboarding-path',
        element: <OnboardingGuard><SuspenseWrapper><OnboardingPathPage /></SuspenseWrapper></OnboardingGuard>,
      },
      {
        path: 'join-practice',
        element: <OnboardingGuard><SuspenseWrapper><JoinPracticePage /></SuspenseWrapper></OnboardingGuard>,
      },
      {
        path: 'pending-approval',
        element: <SuspenseWrapper><PendingApprovalPage /></SuspenseWrapper>,
      },
      {
        path: 'no-access',
        element: <SuspenseWrapper><NoAccessPage /></SuspenseWrapper>,
      },
      {
        path: 'registration-pending',
        element: <SuspenseWrapper><RegistrationPendingPage /></SuspenseWrapper>,
      },
      {
        path: 'registration-rejected',
        element: <SuspenseWrapper><RegistrationRejectedPage /></SuspenseWrapper>,
      },
      {
        path: 'admin/practice-approvals',
        element: (
          <RoleGuard requiredGroups={['medrender-owner']}>
            <SuspenseWrapper><PracticeApprovalsPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'admin/roles',
        element: (
          <RoleGuard requiredGroups={['medrender-owner']}>
            <SuspenseWrapper><RoleManagementPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'admin/data-catalog',
        element: (
          <RoleGuard requiredGroups={['medrender-owner']}>
            <SuspenseWrapper><AdminDataCatalogPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'admin/visit-config',
        element: (
          <RoleGuard requiredGroups={['medrender-owner']}>
            <SuspenseWrapper><VisitConfigAdminPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'admin/class-mappings',
        element: (
          <RoleGuard requiredGroups={['medrender-owner']}>
            <SuspenseWrapper><ClassMappingAdminPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'admin/email-reports',
        element: (
          <RoleGuard requiredGroups={['medrender-owner']}>
            <SuspenseWrapper><EmailReportsAdminPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'admin/heuristics-schedule',
        // Legacy admin path; the new event-driven alarm pipeline replaces
        // the daily heuristics evaluator. Redirect bookmarks to the
        // metrics admin schedule, which controls the surviving daily tick
        // (Safety_Sweeper) per metric-projections design §5.5.
        element: <Navigate to="/admin/metrics" replace />,
      },
      {
        path: 'admin/metrics',
        element: (
          <RoleGuard requiredGroups={['medrender-owner']}>
            <SuspenseWrapper><MetricsAdminPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'admin/computed-metrics',
        element: (
          <RoleGuard requiredGroups={['medrender-owner']}>
            <SuspenseWrapper><ComputedMetricsAdminPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'admin/system-events',
        element: (
          <RoleGuard requiredGroups={['medrender-owner']}>
            <SuspenseWrapper><SystemEventsPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'admin/default-component-access',
        element: (
          <RoleGuard requiredGroups={['medrender-owner']}>
            <SuspenseWrapper><DefaultComponentAccessPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'admin/alarm-daily-report-scheduler',
        element: (
          <RoleGuard requiredGroups={['medrender-owner']}>
            <SuspenseWrapper><AlarmDailyReportSchedulerPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'admin/data-load-display-columns',
        element: (
          <RoleGuard requiredGroups={['medrender-owner']}>
            <SuspenseWrapper><DataLoadDisplayColumnsPage /></SuspenseWrapper>
          </RoleGuard>
        ),
      },
      {
        path: 'admin/alarms',
        element: <Navigate to="/alarms?tab=alarms" replace />,
      },
      {
        path: 'manage-users',
        element: <RoleGuard><SuspenseWrapper><UserManagementPage /></SuspenseWrapper></RoleGuard>,
      },
    ],
  },
];
